import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import type { NowMode } from "../../api/now";
import type { DeeperItem } from "../../lib/diveDeeper";
import { NOW_MODES, NOW_MODE_ICON } from "../../lib/nowModes";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

export interface SavedItem extends DeeperItem {
  /** The words shown on the item chip. */
  text: string;
}

interface Props {
  /** The saved items, whole note first. */
  items: SavedItem[];
  mode: NowMode | null;
  modeWhy?: string;
  /** The mode the AI suggested; modeWhy explains this one only. */
  suggestedMode?: NowMode | null;
  onModeChange: (mode: NowMode) => void;
  onDive: (item: SavedItem, index: number) => void;
  onNotNow: () => void;
}

const CHIP_MAX = 40;
const shortLabel = (text: string) => (text.length > CHIP_MAX ? `${text.slice(0, CHIP_MAX - 1).trimEnd()}…` : text);
// Splits "This sounds like {{mode}}" around the mode so the words can be a button in any word order.
const MARK = "\u2063";

/**
 * The saved card (spec 2026-11-04 §2.2): status, "This sounds like {{mode}}" with the mode
 * words opening the inline mode picker, the reason line, the item picker, Dive deeper / Not now.
 */
const DiveDeeperCard = ({ items, mode, modeWhy, suggestedMode, onModeChange, onDive, onNotNow }: Props) => {
  const { t } = useTranslation();
  const id = useId();
  const heading = useRef<HTMLHeadingElement>(null);
  const firstRadio = useRef<HTMLButtonElement>(null);
  const [picking, setPicking] = useState(false);
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    heading.current?.focus();
  }, []);
  useEffect(() => {
    if (picking) firstRadio.current?.focus();
  }, [picking]);

  const item = items[selected] ?? items[0];
  if (!item) return null;

  const extra = items.length - 1;
  const status =
    extra <= 0
      ? t("now.card.saved")
      : extra === 1
        ? t("now.card.savedPlusKind", { kind: t(`now.card.kind.${items[1].kind}`) })
        : t("now.card.savedPlusCount", { count: extra });
  const [before, after = ""] = t("now.card.soundsLike", { mode: MARK }).split(MARK);

  return (
    <section className="space-y-4" aria-labelledby={`${id}-h`}>
      <p role="status" className="flex items-center gap-1.5 text-sm text-foreground">
        <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" aria-hidden />
        {status}
      </p>

      <div className="space-y-1">
        <h2 id={`${id}-h`} ref={heading} tabIndex={-1} className="text-base font-semibold text-foreground focus:outline-none">
          {mode ? (
            <>
              {before}
              <button
                type="button"
                aria-expanded={picking}
                aria-controls={picking ? `${id}-modes` : undefined}
                onClick={() => setPicking((p) => !p)}
                className="inline-flex min-h-11 items-center rounded text-primary underline decoration-dotted underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {t(`now.mode.${mode}`)}
              </button>
              {after}
            </>
          ) : (
            t("now.card.noMode")
          )}
        </h2>
        {mode && modeWhy && mode === suggestedMode && <p className="text-sm text-foreground/80">{modeWhy}</p>}
      </div>

      {picking && (
        <div id={`${id}-modes`} role="radiogroup" aria-label={t("now.modeLabel")} className="flex flex-wrap gap-1.5">
          {NOW_MODES.map((m, i) => {
            const Icon = NOW_MODE_ICON[m];
            const on = m === mode;
            return (
              <button
                key={m}
                ref={i === 0 ? firstRadio : undefined}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => {
                  onModeChange(m);
                  setPicking(false);
                }}
                className={cn(
                  "inline-flex min-h-11 items-center gap-1.5 rounded-full border px-3 text-xs motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                  on ? "border-primary bg-primary/10 text-foreground" : "border-border text-foreground/80 hover:text-foreground"
                )}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden />
                {t(`now.mode.${m}`)}
              </button>
            );
          })}
        </div>
      )}

      {items.length > 1 && (
        <div className="space-y-1.5">
          <p id={`${id}-into`} className="text-xs text-foreground/80">
            {t("now.card.diveInto")}
          </p>
          <div role="listbox" aria-labelledby={`${id}-into`} className="flex flex-wrap gap-1.5">
            {items.map((it, i) => (
              <button
                key={it.id}
                type="button"
                role="option"
                aria-selected={i === selected}
                onClick={() => setSelected(i)}
                dir="auto"
                className={cn(
                  "min-h-11 rounded-full border px-3 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                  i === selected ? "border-primary/40 bg-primary/10 text-primary" : "border-border text-foreground/80 hover:border-primary/30"
                )}
              >
                {shortLabel(it.text)}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" className="min-h-11" onClick={onNotNow}>
          {t("now.card.notNow")}
        </Button>
        <Button className="rounded-full min-h-11 px-5" onClick={() => onDive(item, selected)}>
          {t("now.card.dive")}
          <ArrowRight className="ms-1.5 h-4 w-4 rtl:rotate-180" aria-hidden />
        </Button>
      </div>
    </section>
  );
};

export default DiveDeeperCard;
